/**
 * WebsiteHubPage.jsx — Unified surface for the public-facing website.
 *
 * Three tabs:
 *   • Pages    – embeds the existing WebsiteTab (hero, sections, theme).
 *   • Bookings – list / confirm / cancel customer bookings.
 *   • Reviews  – moderation queue for tenant reviews.
 *
 * Mirrors the admin UI principles already used by MarketingToolsPage:
 *   blue gradient header → tabs → cards.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Globe, FileText, Calendar, Star, Loader2, Save, Eye, Check, X, Trash2, MessageSquare, Filter, Phone, Bike, Edit2, BookOpen, Plus, Tag, Image as ImageIcon, Upload } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Spinner } from '../../components/ui/spinner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import WebsiteTab from './settings/WebsiteTab';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebsiteHubPage() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'pages';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Footer action surface from WebsiteTab.
  const [websiteActions, setWebsiteActions] = useState(null);
  const handleWebsiteActions = useCallback((a) => setWebsiteActions(a), []);

  // Pending counts (driving the tab badges).
  const [counts, setCounts] = useState({ pending_bookings: 0, pending_reviews: 0 });
  const refreshCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/website/counts`);
      setCounts(res.data || { pending_bookings: 0, pending_reviews: 0 });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const handleTabChange = (val) => {
    setActiveTab(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'pages') next.delete('tab'); else next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-800 to-blue-900 px-5 py-3.5 md:px-6 md:py-4 text-white">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/20 blur-2xl" />
            <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
          </div>
          <div className="relative z-10 flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight font-['Barlow_Condensed'] leading-tight">
                {language === 'en' ? 'Website' : 'ওয়েবসাইট'}
              </h1>
              <p className="text-white/70 text-xs md:text-sm max-w-3xl">
                {language === 'en'
                  ? 'Manage your public site, customer bookings and reviews — all in one place.'
                  : 'আপনার পাবলিক সাইট, কাস্টমার বুকিং এবং রিভিউ এক জায়গায় দেখুন।'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-4 lg:w-[640px]">
            <TabsTrigger value="pages" className="gap-1.5">
              <FileText className="w-4 h-4" />
              {language === 'en' ? 'Pages' : 'পেজ'}
            </TabsTrigger>
            <TabsTrigger value="bookings" className="gap-1.5 relative">
              <Calendar className="w-4 h-4" />
              {language === 'en' ? 'Bookings' : 'বুকিং'}
              {counts.pending_bookings > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                  {counts.pending_bookings}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-1.5 relative">
              <Star className="w-4 h-4" />
              {language === 'en' ? 'Reviews' : 'রিভিউ'}
              {counts.pending_reviews > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                  {counts.pending_reviews}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="blog" className="gap-1.5">
              <BookOpen className="w-4 h-4" />
              {language === 'en' ? 'Blog' : 'ব্লগ'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pages" className="mt-6">
            <WebsiteTab language={language} hideHeaderActions onActionsChange={handleWebsiteActions} />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <BookingsTab language={language} onChange={refreshCounts} />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <ReviewsTab language={language} onChange={refreshCounts} />
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <BlogTab language={language} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating action bar — only on Pages tab when WebsiteTab provided actions. */}
      {activeTab === 'pages' && websiteActions && (
        <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-[var(--admin-sidebar-w,0px)] z-30 pointer-events-none">
          <div className="max-w-5xl mx-auto px-3 md:px-4 pb-3 md:pb-4 pointer-events-auto">
            <div className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-[0_18px_50px_-20px_rgba(15,23,42,0.25)] flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
              <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
                {websiteActions.dirty ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    {language === 'en' ? 'Unsaved changes' : 'অসংরক্ষিত পরিবর্তন'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                    {language === 'en' ? 'All changes saved' : 'সমস্ত পরিবর্তন সংরক্ষিত'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-end">
                {websiteActions.previewHref && (
                  <a
                    href={websiteActions.previewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-slate-200 text-slate-700 hover:bg-slate-50"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">{language === 'en' ? 'Preview' : 'প্রিভিউ'}</span>
                  </a>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => websiteActions.saveDraft?.()}
                  disabled={websiteActions.saving || !websiteActions.dirty}
                  className="gap-1.5"
                >
                  {websiteActions.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {language === 'en' ? 'Save draft' : 'ড্রাফট'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => websiteActions.publish?.()}
                  disabled={websiteActions.publishing}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                >
                  {websiteActions.publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                  {language === 'en' ? 'Publish' : 'প্রকাশ'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

/* ─────────────────────────  Bookings tab  ───────────────────────── */

const BOOKING_FILTERS = [
  { value: 'pending', en: 'Pending', bn: 'অপেক্ষমাণ' },
  { value: 'confirmed', en: 'Confirmed', bn: 'নিশ্চিত' },
  { value: 'completed', en: 'Completed', bn: 'সম্পন্ন' },
  { value: 'cancelled', en: 'Cancelled', bn: 'বাতিল' },
  { value: 'all', en: 'All', bn: 'সব' }
];

function BookingsTab({ language, onChange }) {
  const { toast } = useToast();
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const res = await axios.get(`${API}/bookings`, { params });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({ title: language === 'en' ? 'Failed to load bookings' : 'বুকিং লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [language, toast]);

  useEffect(() => { load(filter); }, [filter, load]);

  async function setStatus(id, status) {
    setBusyId(id);
    try {
      await axios.put(`${API}/bookings/${id}/status`, null, { params: { status } });
      toast({ title: language === 'en' ? 'Booking updated' : 'বুকিং আপডেট হয়েছে' });
      setItems(prev => prev.map(b => b.id === id ? { ...b, status } : b)
        .filter(b => filter === 'all' || b.status === filter));      onChange?.();    } catch (err) {
      toast({ title: language === 'en' ? 'Action failed' : 'কাজ ব্যর্থ', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 mr-1">
          <Filter className="w-3.5 h-3.5" />
          {language === 'en' ? 'Filter' : 'ফিল্টার'}
        </span>
        {BOOKING_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={[
              'h-8 px-3 rounded-md text-sm font-medium transition border',
              filter === f.value
                ? 'bg-blue-700 text-white border-blue-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700'
            ].join(' ')}
          >
            {language === 'en' ? f.en : f.bn}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700">
              {language === 'en' ? 'No bookings here yet' : 'এখনো কোনো বুকিং নেই'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {language === 'en' ? 'New customer bookings will appear here.' : 'নতুন বুকিং এখানে আসবে।'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(b => (
            <BookingCard
              key={b.id}
              booking={b}
              language={language}
              busy={busyId === b.id}
              onStatus={(s) => setStatus(b.id, s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BookingCard({ booking, language, busy, onStatus }) {
  const created = booking.created_at ? new Date(booking.created_at).toLocaleDateString(language === 'bn' ? 'bn-BD' : 'en-GB') : '';
  const statusStyle = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-slate-50 text-slate-600 border-slate-200',
    no_show: 'bg-red-50 text-red-700 border-red-200',
  }[booking.status] || 'bg-slate-50 text-slate-600 border-slate-200';

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{booking.customer_name || '—'}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" />
              <span>{booking.phone}</span>
              {created && <span>· {created}</span>}
            </div>
          </div>
          <span className={['inline-flex items-center px-2 h-6 rounded-md text-[11px] font-medium border', statusStyle].join(' ')}>
            {language === 'en' ? booking.status : (
              { pending: 'অপেক্ষমাণ', confirmed: 'নিশ্চিত', completed: 'সম্পন্ন', cancelled: 'বাতিল', no_show: 'অনুপস্থিত' }[booking.status] || booking.status
            )}
          </span>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-slate-700 space-y-1">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-medium">{booking.preferred_date}</span>
            <span className="text-slate-400">·</span>
            <span>{booking.preferred_time}</span>
          </div>
          {(booking.bike_make || booking.bike_model) && (
            <div className="flex items-center gap-2 text-slate-600">
              <Bike className="w-3.5 h-3.5 text-slate-400" />
              <span>{[booking.bike_make, booking.bike_model].filter(Boolean).join(' ')}</span>
            </div>
          )}
          {booking.service_type && (
            <div className="text-slate-600 text-xs">
              {language === 'en' ? 'Service' : 'সার্ভিস'}: <span className="font-medium text-slate-800">{booking.service_type}</span>
            </div>
          )}
          {booking.notes && (
            <div className="text-slate-500 text-xs italic break-words">"{booking.notes}"</div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
          {booking.status === 'pending' && (
            <Button size="sm" onClick={() => onStatus('confirmed')} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {language === 'en' ? 'Confirm' : 'নিশ্চিত'}
            </Button>
          )}
          {booking.status === 'confirmed' && (
            <Button size="sm" onClick={() => onStatus('completed')} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {language === 'en' ? 'Mark complete' : 'সম্পন্ন'}
            </Button>
          )}
          {booking.status !== 'cancelled' && booking.status !== 'completed' && (
            <Button size="sm" variant="outline" onClick={() => onStatus('cancelled')} disabled={busy} className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
              <X className="w-4 h-4" />
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </Button>
          )}
          <a
            href={`tel:${booking.phone}`}
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <Phone className="w-3.5 h-3.5" />
            {language === 'en' ? 'Call' : 'কল'}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────  Reviews tab  ───────────────────────── */

const REVIEW_FILTERS = [
  { value: 'pending', en: 'Pending', bn: 'অপেক্ষমাণ' },
  { value: 'approved', en: 'Published', bn: 'প্রকাশিত' },
  { value: 'all', en: 'All', bn: 'সব' }
];

function ReviewsTab({ language, onChange }) {
  const { toast } = useToast();
  const [filter, setFilter] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const res = await axios.get(`${API}/reviews`, { params });
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({ title: language === 'en' ? 'Failed to load reviews' : 'রিভিউ লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [language, toast]);

  useEffect(() => { load(filter); }, [filter, load]);

  async function moderate(id, approved) {
    setBusyId(id);
    try {
      await axios.put(`${API}/reviews/${id}/moderate`, null, { params: { approved } });
      toast({ title: approved ? (language === 'en' ? 'Review published' : 'রিভিউ প্রকাশিত') : (language === 'en' ? 'Review hidden' : 'রিভিউ লুকানো') });
      setReviews(prev => prev.map(r => r.id === id ? { ...r, approved } : r)
        .filter(r => filter === 'all' || (filter === 'pending' ? !r.approved : r.approved)));
      onChange?.();
    } catch (err) {
      toast({ title: language === 'en' ? 'Action failed' : 'কাজ ব্যর্থ', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id) {
    setBusyId(id);
    try {
      await axios.delete(`${API}/reviews/${id}`);
      setReviews(prev => prev.filter(r => r.id !== id));
      toast({ title: language === 'en' ? 'Review deleted' : 'রিভিউ মুছে ফেলা হয়েছে' });      onChange?.();    } catch (err) {
      toast({ title: language === 'en' ? 'Delete failed' : 'মুছে ফেলা ব্যর্থ', variant: 'destructive' });
    } finally {
      setBusyId(null);
      setDeleteId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 mr-1">
          <Filter className="w-3.5 h-3.5" />
          {language === 'en' ? 'Filter' : 'ফিল্টার'}
        </span>
        {REVIEW_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={[
              'h-8 px-3 rounded-md text-sm font-medium transition border',
              filter === f.value
                ? 'bg-blue-700 text-white border-blue-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700'
            ].join(' ')}
          >
            {language === 'en' ? f.en : f.bn}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700">
              {language === 'en' ? 'No reviews here yet' : 'এখানে কোনো রিভিউ নেই'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {filter === 'pending'
                ? (language === 'en' ? 'New customer submissions will appear here.' : 'নতুন কাস্টমার রিভিউ এখানে আসবে।')
                : (language === 'en' ? 'Switch filter to see other reviews.' : 'অন্য ফিল্টার দেখুন।')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviews.map(r => (
            <ReviewCard
              key={r.id}
              review={r}
              language={language}
              busy={busyId === r.id}
              onApprove={() => moderate(r.id, true)}
              onHide={() => moderate(r.id, false)}
              onDelete={() => setDeleteId(r.id)}
              onEdited={(patched) => {
                setReviews(prev => prev.map(x => x.id === r.id ? { ...x, ...patched } : x));
                onChange?.();
              }}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{language === 'en' ? 'Delete this review?' : 'এই রিভিউ মুছে ফেলবেন?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en'
                ? 'This will permanently remove the review. This action cannot be undone.'
                : 'এই রিভিউ স্থায়ীভাবে মুছে যাবে। এটি ফেরানো যাবে না।'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => remove(deleteId)}>
              {language === 'en' ? 'Delete' : 'মুছে ফেলুন'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReviewCard({ review, language, busy, onApprove, onHide, onDelete, onEdited }) {
  const stars = Math.max(0, Math.min(5, Number(review.rating) || 0));
  const date = review.created_at ? new Date(review.created_at).toLocaleDateString(language === 'bn' ? 'bn-BD' : 'en-GB') : '';
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: review.name || '', comment: review.comment || '', rating: stars, service: review.service || '' });
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setDraft({ name: review.name || '', comment: review.comment || '', rating: stars, service: review.service || '' });
    setEditing(true);
  }
  async function save() {
    setSaving(true);
    try {
      await axios.put(`${API}/reviews/${review.id}`, draft);
      onEdited?.(draft);
      toast({ title: language === 'en' ? 'Review updated' : 'রিভিউ আপডেট হয়েছে' });
      setEditing(false);
    } catch {
      toast({ title: language === 'en' ? 'Update failed' : 'আপডেট ব্যর্থ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">{language === 'en' ? 'Name' : 'নাম'}</label>
              <Input value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-slate-500">{language === 'en' ? 'Service' : 'সার্ভিস'}</label>
              <Input value={draft.service} onChange={(e) => setDraft(d => ({ ...d, service: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">{language === 'en' ? 'Rating' : 'রেটিং'}</label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map(i => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, rating: i }))}
                  className="p-0.5"
                >
                  <Star className={['w-6 h-6', i <= draft.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'].join(' ')} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">{language === 'en' ? 'Comment' : 'মন্তব্য'}</label>
            <Textarea rows={4} value={draft.comment} onChange={(e) => setDraft(d => ({ ...d, comment: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button size="sm" onClick={save} disabled={saving} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {language === 'en' ? 'Save' : 'সেভ'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{review.name || (language === 'en' ? 'Anonymous' : 'অজ্ঞাত')}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={['w-4 h-4', i <= stars ? 'fill-amber-400 text-amber-400' : 'text-slate-300'].join(' ')} />
                ))}
              </div>
              {date && <span className="text-xs text-slate-400">· {date}</span>}
            </div>
          </div>
          <span className={[
            'inline-flex items-center px-2 h-6 rounded-md text-[11px] font-medium border',
            review.approved
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          ].join(' ')}>
            {review.approved
              ? (language === 'en' ? 'Published' : 'প্রকাশিত')
              : (language === 'en' ? 'Pending' : 'অপেক্ষমাণ')}
          </span>
        </div>

        {review.comment && (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
            {review.comment}
          </p>
        )}

        {(review.phone || review.service) && (
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            {review.service && <span>{language === 'en' ? 'Service' : 'সার্ভিস'}: <span className="text-slate-700 font-medium">{review.service}</span></span>}
            {review.phone && <span>{language === 'en' ? 'Phone' : 'ফোন'}: <span className="text-slate-700 font-medium">{review.phone}</span></span>}
          </div>
        )}

        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          {review.approved ? (
            <Button size="sm" variant="outline" onClick={onHide} disabled={busy} className="gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              {language === 'en' ? 'Hide' : 'লুকান'}
            </Button>
          ) : (
            <Button size="sm" onClick={onApprove} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {language === 'en' ? 'Approve' : 'অনুমোদন'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onDelete} disabled={busy} className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
            <Trash2 className="w-4 h-4" />
            {language === 'en' ? 'Delete' : 'মুছুন'}
          </Button>
          <Button size="sm" variant="outline" onClick={startEdit} disabled={busy} className="gap-1.5 ml-auto">
            <Edit2 className="w-4 h-4" />
            {language === 'en' ? 'Edit' : 'এডিট'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────  Blog tab  ───────────────────────── */

function BlogTab({ language }) {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | published | draft
  const [editing, setEditing] = useState(null); // post object or 'new'
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/posts`);
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast({ title: language === 'en' ? 'Failed to load posts' : 'পোস্ট লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [language, toast]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return posts;
    return posts.filter(p => filter === 'published' ? p.published : !p.published);
  }, [posts, filter]);

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/blog/posts/${deleteId}`);
      setPosts(prev => prev.filter(p => p.id !== deleteId));
      toast({ title: language === 'en' ? 'Post deleted' : 'পোস্ট মুছে ফেলা হয়েছে' });
    } catch {
      toast({ title: language === 'en' ? 'Delete failed' : 'মুছতে ব্যর্থ', variant: 'destructive' });
    } finally {
      setDeleteId(null);
    }
  }

  if (editing) {
    return (
      <BlogEditor
        language={language}
        post={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => {
          setPosts(prev => {
            const idx = prev.findIndex(p => p.id === saved.id);
            if (idx === -1) return [saved, ...prev];
            const next = [...prev]; next[idx] = saved; return next;
          });
          setEditing(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'all', en: 'All', bn: 'সব' },
          { key: 'published', en: 'Published', bn: 'প্রকাশিত' },
          { key: 'draft', en: 'Drafts', bn: 'খসড়া' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`h-8 px-3 rounded-md text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-blue-700 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {language === 'en' ? f.en : f.bn}
            <span className="ml-1.5 text-[11px] opacity-80">
              {f.key === 'all' ? posts.length : posts.filter(p => f.key === 'published' ? p.published : !p.published).length}
            </span>
          </button>
        ))}
        <div className="ml-auto">
          <Button size="sm" onClick={() => setEditing('new')} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            {language === 'en' ? 'New post' : 'নতুন পোস্ট'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>{language === 'en' ? 'No posts yet. Click “New post” to write your first article.' : 'এখনো কোনো পোস্ট নেই।'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <Card key={p.id} className="overflow-hidden flex flex-col">
              {p.cover_image_url ? (
                <img src={p.cover_image_url} alt="" className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-slate-400" />
                </div>
              )}
              <CardContent className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${p.published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {p.published ? (language === 'en' ? 'Published' : 'প্রকাশিত') : (language === 'en' ? 'Draft' : 'খসড়া')}
                  </span>
                  <span className="text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                </div>
                <h3 className="font-semibold text-slate-900 line-clamp-2">{p.title}</h3>
                {p.excerpt && <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>}
                {Array.isArray(p.tags) && p.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.tags.slice(0, 3).map(t => (
                      <span key={t} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        <Tag className="w-2.5 h-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-3 flex items-center gap-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="gap-1.5">
                    <Edit2 className="w-4 h-4" />
                    {language === 'en' ? 'Edit' : 'এডিট'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleteId(p.id)} className="ml-auto gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{language === 'en' ? 'Delete this post?' : 'এই পোস্ট মুছবেন?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en' ? 'This cannot be undone.' : 'এটি পূর্বাবস্থায় ফেরানো যাবে না।'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {language === 'en' ? 'Delete' : 'মুছুন'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ───── Blog editor ───── */

function BlogEditor({ language, post, onCancel, onSaved }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState({
    title: post?.title || '',
    title_bn: post?.title_bn || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    excerpt_bn: post?.excerpt_bn || '',
    body: post?.body || '',
    body_bn: post?.body_bn || '',
    cover_image_url: post?.cover_image_url || '',
    tags: Array.isArray(post?.tags) ? post.tags.join(', ') : '',
    published: !!post?.published
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  function set(patch) { setDraft(d => ({ ...d, ...patch })); }

  async function uploadCover(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/upload/image?image_type=asset`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      set({ cover_image_url: res.data?.url || '' });
    } catch (err) {
      toast({ title: language === 'en' ? 'Upload failed' : 'আপলোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function save(publish) {
    if (!draft.title.trim() || !draft.body.trim()) {
      toast({ title: language === 'en' ? 'Title and body required' : 'শিরোনাম ও মূল লেখা দরকার', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        published: publish ?? draft.published,
        tags: draft.tags.split(',').map(s => s.trim()).filter(Boolean),
        slug: draft.slug.trim() || undefined
      };
      const res = post?.id
        ? await axios.put(`${API}/blog/posts/${post.id}`, payload)
        : await axios.post(`${API}/blog/posts`, payload);
      onSaved?.(res.data);
      toast({ title: language === 'en' ? 'Post saved' : 'পোস্ট সংরক্ষিত' });
    } catch (err) {
      toast({ title: language === 'en' ? 'Save failed' : 'সংরক্ষণ ব্যর্থ', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{post?.id ? (language === 'en' ? 'Edit post' : 'পোস্ট সম্পাদনা') : (language === 'en' ? 'New post' : 'নতুন পোস্ট')}</CardTitle>
          <Button size="sm" variant="outline" onClick={onCancel} className="gap-1.5">
            <X className="w-4 h-4" /> {language === 'en' ? 'Close' : 'বন্ধ'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Title' : 'শিরোনাম'}</label>
            <Input value={draft.title} onChange={e => set({ title: e.target.value })} placeholder="How to clean a chain" />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">শিরোনাম (বাংলা)</label>
            <Input value={draft.title_bn} onChange={e => set({ title_bn: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Slug (optional)' : 'স্লাগ'}</label>
          <Input value={draft.slug} onChange={e => set({ slug: e.target.value })} placeholder="auto-generated from title" />
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Cover image' : 'কভার ছবি'}</label>
          <div className="mt-1.5 flex items-center gap-3">
            {draft.cover_image_url ? (
              <img src={draft.cover_image_url} alt="" className="w-24 h-24 rounded-md object-cover border border-slate-200" />
            ) : (
              <div className="w-24 h-24 rounded-md bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-slate-400" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} className="gap-1.5">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {draft.cover_image_url ? (language === 'en' ? 'Replace' : 'বদলান') : (language === 'en' ? 'Upload' : 'আপলোড')}
              </Button>
              {draft.cover_image_url && (
                <Button type="button" variant="outline" size="sm" onClick={() => set({ cover_image_url: '' })} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 gap-1.5">
                  <Trash2 className="w-4 h-4" /> {language === 'en' ? 'Clear' : 'মুছুন'}
                </Button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadCover} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Excerpt' : 'সারসংক্ষেপ'}</label>
            <Textarea rows={3} value={draft.excerpt} onChange={e => set({ excerpt: e.target.value })} maxLength={280} />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">সারসংক্ষেপ (বাংলা)</label>
            <Textarea rows={3} value={draft.excerpt_bn} onChange={e => set({ excerpt_bn: e.target.value })} maxLength={320} />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Body (Markdown supported)' : 'মূল লেখা'}</label>
          <Textarea rows={10} value={draft.body} onChange={e => set({ body: e.target.value })} placeholder="Write your post here..." />
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">মূল লেখা (বাংলা)</label>
          <Textarea rows={10} value={draft.body_bn} onChange={e => set({ body_bn: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Tags (comma-separated)' : 'ট্যাগ (কমা দিয়ে আলাদা)'}</label>
          <Input value={draft.tags} onChange={e => set({ tags: e.target.value })} placeholder="maintenance, chain, tutorial" />
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {language === 'en' ? 'Save draft' : 'খসড়া সংরক্ষণ'}
          </Button>
          <Button onClick={() => save(true)} disabled={saving} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
            {language === 'en' ? 'Publish' : 'প্রকাশ'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={saving} className="ml-auto">
            {language === 'en' ? 'Cancel' : 'বাতিল'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


